import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useTechAuth } from '@/contexts/TechAuthContext';
import { getTechSpiffs, TechSpiffsResponse } from '@/lib/tech-api-client';

function formatCents(cents: number): string {
  const dollars = Number(cents || 0) / 100;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(dollars);
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function formatDateOnly(value: string | null | undefined): string {
  if (!value) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toISOString().slice(0, 10);
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

function formatSpiffType(raw: string): string {
  const value = String(raw || '').trim().toLowerCase();
  if (value === 'new_sale') return 'New Sale';
  if (value === 'renewal') return 'Renewal';
  if (value === 'upgrade') return 'Upgrade';
  return raw || '—';
}

function formatSpiffStatus(raw: string): { label: string; className: string } {
  const value = String(raw || '').trim().toLowerCase();
  if (value === 'paid') {
    return { label: 'Paid', className: 'bg-green-100 text-green-800 border-green-200' };
  }
  if (value === 'cancelled') {
    return { label: 'Cancelled', className: 'bg-gray-100 text-gray-700 border-gray-200' };
  }
  return { label: 'Pending', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' };
}

function formatMembershipStatus(raw: string | null | undefined): { label: string; className: string } {
  const value = String(raw || '').trim().toLowerCase();
  if (value === 'active') return { label: 'Active', className: 'bg-green-100 text-green-800 border-green-200' };
  if (value === 'past_due') return { label: 'Payment Failed', className: 'bg-red-100 text-red-800 border-red-200' };
  if (value === 'suspended') return { label: 'Suspended', className: 'bg-orange-100 text-orange-800 border-orange-200' };
  if (value === 'pending_payment') {
    return { label: 'Pending Payment', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' };
  }
  if (value === 'cancelled') return { label: 'Cancelled', className: 'bg-gray-100 text-gray-700 border-gray-200' };
  if (value === 'expired') return { label: 'Expired', className: 'bg-gray-100 text-gray-700 border-gray-200' };
  return { label: raw || '—', className: 'bg-gray-100 text-gray-700 border-gray-200' };
}

function Chip({ label, className }: { label: string; className: string }) {
  return (
    <span className={['inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-medium', className].join(' ')}>
      {label}
    </span>
  );
}

export default function TechSpiffsPage() {
  const { accessToken, technician } = useTechAuth();
  const [data, setData] = useState<TechSpiffsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!accessToken) return;

    setLoading(true);
    setError(null);
    try {
      const res = await getTechSpiffs(accessToken);
      setData(res);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const totalsAll = data?.totals?.all_time;
  const totalsYtd = data?.totals?.ytd;
  const projections = data?.projections;

  const history = useMemo(() => data?.history || [], [data?.history]);
  const upcoming = useMemo(() => projections?.upcoming_renewals || [], [projections?.upcoming_renewals]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Spiffs</h1>
          <p className="text-gray-600 mt-1">
            {technician?.full_name ? `Tracking spiffs for ${technician.full_name}.` : 'Tracking spiffs.'}
          </p>
        </div>

        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
          disabled={loading}
        >
          <RefreshCw className={['w-4 h-4', loading ? 'animate-spin' : ''].join(' ')} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">Pending (All-time)</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {totalsAll ? formatCents(totalsAll.pending_cents) : '—'}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">Paid (All-time)</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">{totalsAll ? formatCents(totalsAll.paid_cents) : '—'}</div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">Pending (YTD)</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {totalsYtd ? formatCents(totalsYtd.pending_cents) : '—'}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">Paid (YTD)</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">{totalsYtd ? formatCents(totalsYtd.paid_cents) : '—'}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">Renewal spiffs (next 30 days)</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {projections ? formatCents(projections.next_30_days.amount_cents) : '—'}
          </div>
          {projections && <div className="mt-2 text-xs text-gray-500">{projections.next_30_days.count} renewals</div>}
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">Renewal spiffs (next 60 days)</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {projections ? formatCents(projections.next_60_days.amount_cents) : '—'}
          </div>
          {projections && <div className="mt-2 text-xs text-gray-500">{projections.next_60_days.count} renewals</div>}
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">Renewal spiffs (next 90 days)</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {projections ? formatCents(projections.next_90_days.amount_cents) : '—'}
          </div>
          {projections && <div className="mt-2 text-xs text-gray-500">{projections.next_90_days.count} renewals</div>}
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">Renewal spiffs (next 12 months)</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {projections ? formatCents(projections.next_365_days.amount_cents) : '—'}
          </div>
          {projections && <div className="mt-2 text-xs text-gray-500">{projections.next_365_days.count} renewals</div>}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-gray-900">Upcoming renewal spiffs</h2>
          {projections && (
            <div className="text-xs text-gray-500">
              Active memberships: {projections.active_memberships} • Goal: {projections.base_goal.goal}
            </div>
          )}
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-4">Renewal date</th>
                <th className="py-2 pr-4">Customer</th>
                <th className="py-2 pr-4">Plan</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2">Notes</th>
              </tr>
            </thead>
            <tbody>
              {upcoming.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-6 text-gray-500">
                    No upcoming renewals found.
                  </td>
                </tr>
              ) : (
                upcoming.map((row) => {
                  const status = formatMembershipStatus(row.status);

                  return (
                    <tr key={row.membership_id} className="border-t border-gray-100">
                      <td className="py-3 pr-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{formatDateOnly(row.next_renewal_date)}</div>
                        <div className="text-xs text-gray-500">in {row.days_until} days</div>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="font-medium text-gray-900">{row.customer_name || '—'}</div>
                        <div className="text-xs text-gray-500">{row.membership_number || row.membership_id}</div>
                      </td>
                      <td className="py-3 pr-4">{row.plan_name || '—'}</td>
                      <td className="py-3 pr-4">
                        <div className="flex flex-wrap gap-2">
                          <Chip label={status.label} className={status.className} />
                          {row.at_risk && <Chip label="At risk" className="bg-red-100 text-red-800 border-red-200" />}
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="text-xs text-gray-500">next billing: {formatDateOnly(row.next_billing_date)}</div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-lg font-semibold text-gray-900">Spiff history</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-4">Earned</th>
                <th className="py-2 pr-4">Type</th>
                <th className="py-2 pr-4">Customer</th>
                <th className="py-2 pr-4">Amount</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-6 text-gray-500">
                    No spiff history yet.
                  </td>
                </tr>
              ) : (
                history.map((row) => {
                  const status = formatSpiffStatus(row.status);
                  const customer = row.customer_name ||
                    [row.membership?.customer_first_name, row.membership?.customer_last_name].filter(Boolean).join(' ');

                  return (
                    <tr key={row.id} className="border-t border-gray-100">
                      <td className="py-3 pr-4 whitespace-nowrap">{formatDateTime(row.earned_at)}</td>
                      <td className="py-3 pr-4">{formatSpiffType(row.spiff_type)}</td>
                      <td className="py-3 pr-4">
                        <div className="font-medium text-gray-900">{customer || '—'}</div>
                        <div className="text-xs text-gray-500">{row.membership?.membership_number || row.membership_id || ''}</div>
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap">{formatCents(row.amount_cents)}</td>
                      <td className="py-3">
                        <Chip label={status.label} className={status.className} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
